import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useOnWindowResize } from './useOnWindowResize'

/**
 * Logical placement relative to the trigger element.
 * - bottom/top: panel above or below the trigger
 * - left/right: panel extends to the left or right side of the trigger
 *
 * Note: actual pixel offsets (gaps) are handled by CSS. This hook only
 * determines the side and returns a class-friendly token.
 */
export type SmartPosition =
  | 'bottom-left'
  | 'bottom-right'
  | 'top-left'
  | 'top-right'

type HorizontalPref = 'left' | 'right'
type VerticalPref = 'top' | 'bottom'

interface Options {
  /**
   * Whether the floating panel is currently mounted/visible.
   * When true, the hook measures and updates the best placement.
   */
  isOpen: boolean
  /**
   * Ref to the trigger/anchor element the panel is positioned against.
   * This must point to a real DOM element (e.g., a button) when open.
   */
  triggerRef: React.RefObject<HTMLElement | null>
  /**
   * CSS selector for the horizontal boundary container.
   * Defaults to the nearest <main> (or document <main>), then body.
   * Use this when your app has an inner scrolling/content area.
   */
  boundarySelector?: string
  /**
   * Horizontal preference when both sides fit. Default 'left'.
   */
  preferHorizontal?: HorizontalPref
  /**
   * Vertical preference when both sides fit. Default 'bottom'.
   */
  preferVertical?: VerticalPref
  /**
   * Attach a capturing scroll listener to recompute on scroll. Default true.
   */
  listenToScroll?: boolean
}

/**
 * Compute a robust placement token for a floating panel relative to a trigger.
 * - Constrains horizontally to a boundary container (e.g., main content).
 * - Recomputes on window resize and (optionally) on scroll.
 *
 * Returns:
 * - panelRef: attach to the floating panel element to allow measurement
 * - position: SmartPosition token like 'bottom-left'
 * - hasMeasured: true once an initial measurement has happened (useful to hide until measured)
 * - recompute: imperative trigger to recompute placement on demand
 */
export const useSmartPosition = (opts: Options) => {
  const {
    isOpen,
    triggerRef,
    boundarySelector,
    preferHorizontal = 'left',
    preferVertical = 'bottom',
    listenToScroll = true,
  } = opts

  const panelRef = useRef<HTMLDivElement | null>(null)
  const [position, setPosition] = useState<SmartPosition>(
    `${preferVertical}-${preferHorizontal}` as SmartPosition,
  )
  const [hasMeasured, setHasMeasured] = useState(false)

  const getBoundary = useCallback((): HTMLElement => {
    const trigger = triggerRef.current
    if (!trigger) return document.body

    const mainCandidate =
      trigger.closest('main') || document.querySelector('main')
    const preferred = boundarySelector
      ? trigger.closest(boundarySelector) ||
        document.querySelector(boundarySelector)
      : null

    return (
      (preferred as HTMLElement) ||
      (mainCandidate as HTMLElement) ||
      document.body
    )
  }, [boundarySelector, triggerRef])

  const computePosition = useCallback(() => {
    const trigger = triggerRef.current
    const panel = panelRef.current
    if (!trigger || !panel) return

    const btnRect = trigger.getBoundingClientRect()
    const panelRect = panel.getBoundingClientRect()
    const boundary = getBoundary()
    const boundaryRect = boundary.getBoundingClientRect()

    const viewportBottom = window.innerHeight

    // Vertical available space within current viewport & boundary
    const spaceAbove = btnRect.top - Math.max(boundaryRect.top, 0)
    const spaceBelow =
      Math.min(boundaryRect.bottom, viewportBottom) - btnRect.bottom

    // Horizontal available space constrained to boundary width
    const spaceLeft = btnRect.left - Math.max(boundaryRect.left, 0)
    const spaceRight =
      Math.min(boundaryRect.right, window.innerWidth) - btnRect.right

    // Decide vertical side
    const vertical: VerticalPref =
      spaceBelow >= panelRect.height
        ? 'bottom'
        : spaceAbove >= panelRect.height
          ? 'top'
          : spaceBelow >= spaceAbove
            ? 'bottom'
            : 'top'

    // Decide horizontal side, prefer configured when both fit
    let horizontal: HorizontalPref
    const fitsLeft = spaceLeft >= panelRect.width
    const fitsRight = spaceRight >= panelRect.width

    if (fitsLeft && fitsRight) {
      horizontal = preferHorizontal
    } else if (fitsLeft) {
      horizontal = 'left'
    } else if (fitsRight) {
      horizontal = 'right'
    } else {
      horizontal = spaceLeft >= spaceRight ? 'left' : 'right'
    }

    setPosition(`${vertical}-${horizontal}` as SmartPosition)
    setHasMeasured(true)
  }, [getBoundary, preferHorizontal, triggerRef])

  // Recompute on resize
  useOnWindowResize(() => {
    if (isOpen) computePosition()
  })

  // Recompute on scroll (capture phase to get scrolls on ancestors)
  useEffect(() => {
    if (!listenToScroll) return
    if (!isOpen) return
    const onScroll = () => computePosition()
    window.addEventListener('scroll', onScroll, true)
    return () => window.removeEventListener('scroll', onScroll, true)
  }, [computePosition, isOpen, listenToScroll])

  // Compute when opening
  useEffect(() => {
    if (!isOpen) {
      const id = window.requestAnimationFrame(() => {
        setHasMeasured(false)
        setPosition(`${preferVertical}-${preferHorizontal}` as SmartPosition)
      })
      return () => window.cancelAnimationFrame(id)
    }
    const id = window.requestAnimationFrame(computePosition)
    return () => window.cancelAnimationFrame(id)
  }, [computePosition, isOpen, preferHorizontal, preferVertical])

  return useMemo(
    () => ({ panelRef, position, hasMeasured, recompute: computePosition }),
    [hasMeasured, position, computePosition],
  )
}

export default useSmartPosition
